import React, { useState } from "react";

import { FormSelect } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import TableTemplate from "@components/Table/TableTemplate";
import { USERS_ALL_ENDPOINT } from "@constants/Endpoints";
import { Roles } from "@constants/Roles";
import { CheckCircle, DoDisturb, Warning, WatchLater } from "@mui/icons-material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { SelectSchoolModal } from "@pages/Logged/Common/SchoolStats/SelectSchoolModal/SelectSchoolModal";

import { EditModal } from "./EditModal/EditModal";
import { EditRolesModal } from "./EditRolesModal/EditRolesModal";
import { ManageSchoolModal } from "./ManageSchoolModal/ManageSchoolModal";
import "./UsersAdministrationPage.scss";

/**
 * UsersAdministrationPageView component renders a user administration page
 * with a table displaying user data and various modals for managing users.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onEditUserAction - Callback function triggered when the "Edit User" action is performed.
 * @param {Function} props.onUserBlockAction - Callback function triggered when the "Block User" action is performed.
 * @param {Function} props.onEditRolesAction - Callback function triggered when the "Edit Roles" action is performed.
 * @param {Function} props.onManageSchoolsAction - Callback function triggered when the "Manage Schools" action is performed.
 * @param {Function} props.onActiveUserAction - Callback function triggered when the "Activate User" action is performed.
 * @param {Function} props.onTableTemplateDelete - Callback function triggered when a row is deleted from the table.
 * @param {React.ChangeEventHandler<HTMLSelectElement>} props.onFilterUserStateChange - Callback function triggered when the user status type filter changes.
 * @param {Function} props.onTableTemplateClearExtraFiltersParams - Callback function triggered to clear extra filter parameters.
 * @param {Function} props.onHideEditModal - Callback function triggered to hide the edit modal.
 * @param {Function} props.onEditModalSubmit - Callback function triggered when the edit modal form is submitted.
 * @param {Function} props.onHideEditRolesModal - Callback function triggered to hide the edit roles modal.
 * @param {Function} props.onEditRolesModalSubmit - Callback function triggered when the edit roles modal form is submitted.
 * @param {Function} props.onManageSchoolsModalHide - Callback function triggered to hide the manage schools modal.
 * @param {Function} props.onManageSchoolsModalSubmit - Callback function triggered when the manage schools modal form is submitted.
 * @param {React.MouseEventHandler<HTMLSpanElement>} props.onSchoolSelectedClearClick - Callback function triggered when the school assigned filter is cleared.
 * @param {React.MouseEventHandler<HTMLDivElement>} props.onFilterSelectSchoolClick - Callback function triggered when the school select filter changes.
 * @param {Function} props.onSchoolSelectModalHide - Callback function triggered to hide the school select modal.
 * @param {React.MutableRefObject} props.tableTemplateRef - Reference to the table template component.
 * @param {boolean} props.showEditModal - Determines whether the edit modal is visible.
 * @param {boolean} props.showEditRolesModal - Determines whether the edit roles modal is visible.
 * @param {boolean} props.showManageSchoolsModal - Determines whether the manage schools modal is visible.
 * @param {Object} props.activeUser - The currently active user being managed in the modals.
 * @param {Object} props.userData - The data of the logged-in user.
 * @param {string} props.userData.role - The role of the logged-in user.
 * @param {boolean} props.userIsEnabled - The currently selected user status type filter value.
 * @param {boolean} props.userNotAllowed - The currently selected user allowed status filter value.
 * @param {boolean} props.showSchoolSelectModal - Determines whether the school select modal is visible.
 * @param {Array} props.schoolList - The list of schools available for selection.
 * @param {Object} props.schoolSelected - The currently selected school.
 * @param {Function} props.setSchoolSelected - Callback function to set the selected school.
 */
function UsersAdministrationPageView(props) {
    const [t] = useTranslation();

    //#region Icons
    const OkayIcon = () => (
        <span className="icon" title={t("Enabled")}>
            <CheckCircle />
        </span>
    );
    const WarningIcon = () => (
        <span className="icon" title={t("Warning")}>
            <Warning />
        </span>
    );
    const PendingIcon = () => (
        <span className="icon pending" title={t("Pending")}>
            <WatchLater />
        </span>
    );
    const DisabledIcon = () => (
        <span className="icon" title={t("Disabled")}>
            <DoDisturb />
        </span>
    );
    const UncheckedIcon = () => (
        <span className="icon" title={t("Unchecked")}>
            <RadioButtonUncheckedIcon />
        </span>
    );

    //#endregion

    const [columns] = useState([
        {
            name: "firstName",
            headerCaption: t("Name"),
            size: "normal-x",
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "preposition",
            headerCaption: t("Preposition"),
            size: "small-s",
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "lastName",
            headerCaption: t("LastName"),
            size: "normal-x",
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "roleNames",
            headerCaption: t("Role"),
            size: "large",
            caption: (x) => (
                <div className="role-wrapper">
                    {x.roleNames.map((role, idx) => {
                        const fullName = t(role) + " (" + role + ")";
                        return (
                            <div key={"role-" + idx} className="role" title={fullName}>
                                {fullName}
                            </div>
                        );
                    })}
                </div>
            ),
            canSort: true,
            canFilter: false,
            canEdit: false,
        },
        {
            name: "email",
            headerCaption: t("Email"),
            size: "medium",
            canSort: true,
            canFilter: true,
            canEdit: false,
        },
        {
            name: "isUserAllowed",
            headerCaption: t("UserAllowed"),
            textAlign: "center",
            size: "small",
            caption: (x) => (x.isEnabled ? <OkayIcon /> : x.isUserAllowed ? <PendingIcon /> : <DisabledIcon />),
            canSort: false,
            canFilter: false,
            canEdit: false,
        },
        {
            name: "hasActionsEnabled",
            headerCaption: t("Actions"),
            textAlign: "center",
            size: "small",
            caption: (x) => (x.hasActionsEnabled ? <OkayIcon /> : <UncheckedIcon />),
            canSort: false,
            canFilter: false,
            canEdit: false,
        },
        {
            name: "isExternalUser",
            headerCaption: t("ExternalUser"),
            textAlign: "center",
            size: "small",
            caption: (x) => (x.isExternalUser ? <OkayIcon /> : <UncheckedIcon />),
            canSort: false,
            canFilter: false,
            canEdit: false,
        },
        {
            name: "havePendingSchools",
            headerCaption: t("PendingSchools"),
            textAlign: "center",
            size: "small",
            caption: (x) => (x.havePendingSchools ? <WarningIcon /> : <UncheckedIcon />),
            canSort: false,
            canFilter: false,
            canEdit: false,
        },
    ]);

    const customActions = [
        {
            name: t("EditUser"),
            action: props.onEditUserAction,
            hide: () =>
                // @ts-ignore
                props.userData.role !== Roles.Admin && props.userData.role !== Roles.IddinkUserManager,
        },
        {
            name: t("ActivateUser"),
            action: props.onActiveUserAction,
            hide: (row) => row.isEnabled,
        },
        {
            name: t("BlockUser"),
            action: props.onUserBlockAction,
            hide: (row) => !row.isUserAllowed || row.role === Roles.Admin,
        },
        {
            name: t("EditRoles"),
            action: props.onEditRolesAction,
            hide: (row) => !row.isEnabled,
        },
        {
            name: t("ManageSchools"),
            action: props.onManageSchoolsAction,
            hide: (row) => {
                return row.isEnabled === false || row.role !== Roles.SchoolManager;
            },
        },
    ];

    return (
        <div className="admin-overview-page">
            <TableTemplate
                ref={props.tableTemplateRef}
                // @ts-ignore
                title={t("UsersOverview")}
                endpoint={USERS_ALL_ENDPOINT}
                columns={columns}
                uniqueId="id"
                paginator={true}
                customActions={customActions}
                onDelete={props.onTableTemplateDelete}
                extraFilters={[
                    {
                        name: "userState",
                        headerCaption: t("UserState"),
                        component: (
                            <FormSelect onChange={props.onFilterUserStateChange}>
                                <option value="0">{t("All")}</option>
                                <option value="1">{t("Activated")}</option>
                                <option value="2">{t("Pending")}</option>
                                <option value="3">{t("Blocked")}</option>
                            </FormSelect>
                        ),
                    },
                    {
                        name: "schoolAssigned",
                        headerCaption: t("SchoolAssigned"),
                        value: props.schoolSelected ? props.schoolSelected.name : "",
                        component: (
                            <div className="select-school">
                                <div className="select-school-wrapper" onClick={props.onFilterSelectSchoolClick}>
                                    <div className="select-school">
                                        <div className="school-number">
                                            {props.schoolSelected ? props.schoolSelected.schoolNumber : "-"}
                                        </div>
                                        <div className="school-name">
                                            {props.schoolSelected ? props.schoolSelected.name : t("SelectSchool")}
                                        </div>
                                    </div>
                                </div>
                                {props.schoolSelected && (
                                    <span
                                        className="select-school-clear-btn"
                                        onClick={props.onSchoolSelectedClearClick}
                                    >
                                        {t("Clear")}
                                    </span>
                                )}
                            </div>
                        ),
                    },
                    {
                        name: "isEnabled",
                        value: props.userIsEnabled,
                        hide: true,
                    },
                    {
                        name: "userNotAllowed",
                        value: props.userNotAllowed,
                        hide: true,
                    },
                ]}
                clearExtraFiltersParams={props.onTableTemplateClearExtraFiltersParams}
            />
            {props.showEditModal && (
                <EditModal
                    show={props.showEditModal}
                    user={props.activeUser}
                    onHide={props.onHideEditModal}
                    submit={props.onEditModalSubmit}
                />
            )}
            {props.showEditRolesModal && (
                <EditRolesModal
                    show={props.showEditRolesModal}
                    user={props.activeUser}
                    onHide={props.onHideEditRolesModal}
                    submit={props.onEditRolesModalSubmit}
                />
            )}
            {props.showManageSchoolsModal && (
                <ManageSchoolModal
                    show={props.showManageSchoolsModal}
                    user={props.activeUser}
                    onHide={props.onManageSchoolsModalHide}
                    submit={props.onManageSchoolsModalSubmit}
                />
            )}
            {props.showSchoolSelectModal && (
                <SelectSchoolModal
                    show={props.showSchoolSelectModal}
                    schoolList={props.schoolList}
                    schoolSelected={props.schoolSelected ? props.schoolSelected : {}}
                    setSchoolSelected={props.setSchoolSelected}
                    onHide={props.onSchoolSelectModalHide}
                />
            )}
        </div>
    );
}

export default UsersAdministrationPageView;
